import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FocalImage } from "@/components/ui/focal-image";
import type { RecipeBox } from "@/lib/types";
import { BookOpen, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import type { MouseEvent } from "react";

interface RecipeBoxCardListProps {
    readonly recipeBox: RecipeBox;
    readonly onEdit?: (recipeBox: RecipeBox) => void;
}

/**
 * List view variant of RecipeBoxCard - displays recipe box in a horizontal list format
 */
export function RecipeBoxCardList({ recipeBox, onEdit }: RecipeBoxCardListProps) {
    const imageUrl = recipeBox.imageUrl || "https://picsum.photos/seed/placeholder/480/320";

    const handleEditClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        onEdit?.(recipeBox);
    };

    return (
        <Link to={`/recipe-box/${recipeBox.id}`} className="relative block h-full">
            <Card className="group relative flex h-24 flex-row overflow-hidden transition-all duration-200 ease-in-out hover:shadow-md">
                <div className="relative w-24 shrink-0 overflow-hidden">
                    <FocalImage
                        src={imageUrl}
                        alt={recipeBox.title}
                        className="w-full h-full"
                        data-ai-hint={recipeBox.imageHint}
                        focalPointX={recipeBox.imageFocalPointX}
                        focalPointY={recipeBox.imageFocalPointY}
                        zoom={recipeBox.imageZoom}
                    />
                </div>
                <CardContent className="flex flex-1 flex-col justify-center gap-1 p-2">
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                            <h3 className="font-headline text-sm font-semibold leading-snug truncate">{recipeBox.title}</h3>
                            {recipeBox.description ? (
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                                    {recipeBox.description}
                                </p>
                            ) : null}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <div className="flex items-center text-xs text-muted-foreground">
                                <BookOpen className="mr-1 h-3.5 w-3.5" />
                                <span>{recipeBox.recipeCount} recipes</span>
                            </div>
                            {onEdit && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={handleEditClick}
                                    aria-label={`Edit ${recipeBox.title}`}
                                >
                                    <Pencil className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
}

